import React, { useMemo, useState, useEffect, useRef } from "react";
import {
    Box,
    Paper,
    Typography,
    IconButton,
    TextField,
    InputAdornment,
    Divider,
    Stack,
    Avatar,
    List,
    ListItemButton,
    Badge,
    Drawer,
    Chip,
    useTheme,
    useMediaQuery,
    Skeleton,
} from "@mui/material";

import SearchIcon from "@mui/icons-material/Search";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CloseIcon from "@mui/icons-material/Close";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";

import { useSearchParams } from "react-router-dom";
import { useLeadsList } from "../hooks/useLeadsList";
import { useLeadHistory } from "../hooks/useLeadHistory";
import { useLeadDetails } from "../hooks/useLeadDetails";
import LeadProfileSections, { SectionTitle, DetailRow } from "../components/leads/LeadProfileSections";

function initials(name = "") {
    return name
        .split(" ")
        .filter(Boolean)
        .slice(0, 2)
        .map((p) => p[0].toUpperCase())
        .join("");
}

function formatTime(ts) {
    if (!ts) return "";
    return new Date(ts).toLocaleTimeString("pt-BR", {
        hour: "2-digit",
        minute: "2-digit",
    });
}

function dateLabelFromTs(ts) {
    if (!ts) return null;
    const d = new Date(ts);
    const today = new Date();
    const yesterday = new Date();
    yesterday.setDate(today.getDate() - 1);

    const sameDay = (a, b) =>
        a.getFullYear() === b.getFullYear() &&
        a.getMonth() === b.getMonth() &&
        a.getDate() === b.getDate();

    if (sameDay(d, today)) return "Hoje";
    if (sameDay(d, yesterday)) return "Ontem";
    return d.toLocaleDateString("pt-BR", { day: "2-digit", month: "2-digit", year: "numeric" });
}

function buildMessagesWithSeparators(messages) {
    const items = [];
    let lastLabel = null;

    for (const msg of messages) {
        const label = dateLabelFromTs(msg.timestamp);
        if (label && label !== lastLabel) {
            items.push({ type: "separator", label, key: `sep-${label}` });
            lastLabel = label;
        }
        items.push({ type: "message", ...msg });
    }

    return items;
}

function DateSeparator({ label }) {
    return (
        <Box
            sx={{
                display: "flex",
                alignItems: "center",
                my: 1.5,
                gap: 1,
            }}
        >
            <Box sx={{ flex: 1, height: "1px", bgcolor: "var(--border)" }} />
            <Box
                sx={{
                    px: 1.5,
                    py: 0.4,
                    borderRadius: 999,
                    bgcolor: "rgba(33,49,89,0.08)",
                    border: "1px solid var(--border)",
                }}
            >
                <Typography sx={{ fontSize: 11, fontWeight: 600, color: "var(--cise-text-muted)" }}>
                    {label}
                </Typography>
            </Box>
            <Box sx={{ flex: 1, height: "1px", bgcolor: "var(--border)" }} />
        </Box>
    );
}

function MessageBubble({ from, text, time }) {
    const isMe = from === "me";
    return (
        <Box
            sx={{
                display: "flex",
                justifyContent: isMe ? "flex-end" : "flex-start",
                mb: 1.2,
            }}
        >
            <Box
                sx={{
                    maxWidth: "78%",
                    px: 1.5,
                    py: 1,
                    borderRadius: 0,
                    borderTopLeftRadius: isMe ? 12 : 0,
                    borderTopRightRadius: isMe ? 0 : 12,
                    borderBottomRightRadius: 12,
                    borderBottomLeftRadius: 12,
                    background: isMe ? "rgba(33,49,89,0.12)" : "var(--branco)",
                    border: isMe ? "1px solid rgba(33,49,89,0.18)" : "1px solid var(--border)",
                }}
            >
                <Typography sx={{ fontSize: 14, color: "var(--cise-text)" }}>{text}</Typography>
                <Typography
                    sx={{
                        fontSize: 11,
                        color: "var(--cise-text-muted)",
                        mt: 0.5,
                        textAlign: "right",
                    }}
                >
                    {time}
                </Typography>
            </Box>
        </Box>
    );
}

const STATUS_COLOR = {
    "Em conversa": "info",
    "Sem resposta": "warning",
    "Fechado": "success",
};

const PRIORITY_COLOR = {
    Alta: "error",
    Média: "warning",
    Baixa: "default",
};

function LeadDetailsDrawer({ open, onClose, lead }) {
    const { details, loading: loadingDetails } = useLeadDetails(open ? lead?.waId : null);

    if (!lead) return null;

    const lastAt = lead.lastMessageAt
        ? new Date(lead.lastMessageAt).toLocaleString("pt-BR", {
              day: "2-digit", month: "2-digit", year: "numeric",
              hour: "2-digit", minute: "2-digit",
          })
        : null;

    return (
        <Drawer
            anchor="right"
            open={open}
            onClose={onClose}
            PaperProps={{
                sx: {
                    width: { xs: "100%", sm: 380 },
                    display: "flex",
                    flexDirection: "column",
                    bgcolor: "var(--bg)",
                },
            }}
        >
            {/* Header */}
            <Box sx={{ px: 2.5, py: 2, display: "flex", alignItems: "center", justifyContent: "space-between", borderBottom: "1px solid var(--border)", bgcolor: "var(--branco)" }}>
                <Typography sx={{ fontWeight: 900, fontSize: 15, color: "var(--cise-blue)" }}>
                    Detalhes do Lead
                </Typography>
                <IconButton size="small" onClick={onClose}>
                    <CloseIcon fontSize="small" />
                </IconButton>
            </Box>

            <Box sx={{ flex: 1, overflowY: "auto" }}>
                {/* Perfil do contato */}
                <Box sx={{ px: 2.5, py: 2.5, bgcolor: "var(--branco)", borderBottom: "1px solid var(--border)" }}>
                    <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
                        <Avatar sx={{ width: 52, height: 52, bgcolor: "rgba(33,49,89,0.16)", color: "var(--cise-blue)", fontWeight: 900, fontSize: 18 }}>
                            {lead.name.split(" ").filter(Boolean).slice(0, 2).map((p) => p[0].toUpperCase()).join("")}
                        </Avatar>
                        <Box>
                            <Typography sx={{ fontWeight: 900, fontSize: 16 }}>{lead.name}</Typography>
                            <Typography sx={{ fontSize: 13, color: "var(--cise-text-muted)" }}>{lead.phone}</Typography>
                        </Box>
                    </Stack>
                    <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ gap: 0.8 }}>
                        <Chip label={lead.status} color={STATUS_COLOR[lead.status] || "default"} size="small" sx={{ fontWeight: 700 }} />
                        <Chip label={`Prioridade ${lead.priority}`} color={PRIORITY_COLOR[lead.priority] || "default"} size="small" sx={{ fontWeight: 700 }} />
                        <Chip label={lead.stage} variant="outlined" size="small" sx={{ fontWeight: 700 }} />
                    </Stack>
                </Box>

                <Box sx={{ px: 2.5, py: 1.5 }}>
                    {/* Atendimento */}
                    <SectionTitle>Atendimento</SectionTitle>
                    <Box sx={{ bgcolor: "var(--branco)", borderRadius: 1, border: "1px solid var(--border)", px: 2, py: 1, mb: 1 }}>
                        <DetailRow label="Agente" value={lead.owner} />
                        <DetailRow label="Etapa" value={lead.stage} />
                        <DetailRow label="Última interação" value={lastAt} />
                        <DetailRow label="Atendimento" value={lead.standby ? "Encerrado (standby)" : "Ativo"} />
                    </Box>

                    {loadingDetails ? (
                        <Box sx={{ pt: 2 }}>
                            {[80, 120, 100].map((w, i) => <Skeleton key={i} variant="text" width={`${w}%`} height={18} sx={{ mb: 0.5 }} />)}
                        </Box>
                    ) : details ? (
                        <LeadProfileSections details={details} phone={lead.phone} />
                    ) : (
                        <Typography sx={{ fontSize: 12, color: "var(--cise-text-muted)", pt: 2 }}>
                            O atendimento ainda não gerou dados de perfil.
                        </Typography>
                    )}
                </Box>
            </Box>
        </Drawer>
    );
}

export default function ConversationsPage() {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("md"));

    const [searchParams] = useSearchParams();
    const openId = searchParams.get("open");

    const [query, setQuery] = useState("");
    const [selectedId, setSelectedId] = useState(openId || null);
    const [mobileView, setMobileView] = useState("list");

    const { leads, loading } = useLeadsList();
    const { messages, loading: loadingMessages } = useLeadHistory(selectedId);

    const [detailsOpen, setDetailsOpen] = useState(false);

    const selectedLead = useMemo(
        () => leads.find((l) => l.id === selectedId) || null,
        [leads, selectedId]
    );

    const messagesEndRef = useRef(null);

    useEffect(() => {
        if (!loadingMessages && messages.length > 0) {
            messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
        }
    }, [messages, loadingMessages, selectedId]);

    useEffect(() => {
        if (openId) {
            setSelectedId(openId);
            if (isMobile) setMobileView("chat");
        }
    }, [openId, isMobile]);

    useEffect(() => {
        const onKeyDown = (e) => {
            if (e.key === "Escape") closeChat();
        };
        window.addEventListener("keydown", onKeyDown);
        return () => window.removeEventListener("keydown", onKeyDown);
    }, []);

    const conversations = useMemo(() => {
        const q = query.trim().toLowerCase();
        return leads
            .map((l) => ({
                id: l.id,
                name: l.name,
                last: l.lastUserMessage || "Sem mensagens",
                time: formatTime(l.lastMessageAt),
                unread: l.unread,
                owner: l.owner,
                stage: l.stage,
            }))
            .filter((c) => {
                if (!q) return true;
                return c.name.toLowerCase().includes(q) || c.last.toLowerCase().includes(q);
            });
    }, [leads, query]);

    const selected = useMemo(
        () => conversations.find((c) => c.id === selectedId) || null,
        [conversations, selectedId]
    );

    const openChat = (id) => {
        setSelectedId(id);
        if (isMobile) setMobileView("chat");
    };

    const closeChat = () => {
        setSelectedId(null);
        setMobileView("list");
    };

    const LeftList = (
        <Paper
            elevation={0}
            sx={{
                border: "1px solid var(--border)",
                background: "var(--branco)",
                overflow: "hidden",
                height: "100%",
                minHeight: 0,
                display: "flex",
                flexDirection: "column",
            }}
        >
            <Box sx={{ px: 2, py: 1.5, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                <Typography sx={{ fontWeight: 800, color: "var(--cise-text)" }}>Conversas</Typography>
                <IconButton size="small" title="Mais">
                    <MoreVertIcon fontSize="small" />
                </IconButton>
            </Box>

            <Box sx={{ px: 2, pb: 1.5 }}>
                <TextField
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    placeholder="Pesquisar conversa"
                    size="small"
                    fullWidth
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchIcon fontSize="small" />
                            </InputAdornment>
                        ),
                    }}
                    sx={{
                        "& .MuiOutlinedInput-root": {
                            borderRadius: 999,
                            background: "var(--bg)",
                        },
                    }}
                />
            </Box>

            <Divider />

            <List sx={{ p: 0, flex: 1, minHeight: 0, overflowY: "auto" }}>
                {conversations.map((c) => {
                    const active = c.id === selectedId;
                    return (
                        <ListItemButton
                            key={c.id}
                            onClick={() => openChat(c.id)}
                            sx={{
                                px: 2,
                                py: 1.4,
                                alignItems: "center",
                                background: active ? "rgba(33,49,89,0.10)" : "transparent",
                                "&:hover": { background: active ? "rgba(33,49,89,0.12)" : "rgba(15,23,42,0.04)" },
                            }}
                        >
                            <Avatar sx={{ width: 44, height: 44, mr: 1.5, bgcolor: "rgba(33,49,89,0.16)", color: "var(--cise-blue)" }}>
                                {initials(c.name)}
                            </Avatar>

                            <Box sx={{ flex: 1, minWidth: 0 }}>
                                <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
                                    <Typography sx={{ fontWeight: 700, fontSize: 14 }} noWrap>
                                        {c.name}
                                    </Typography>
                                    <Typography sx={{ fontSize: 12, color: "var(--cise-text-muted)" }}>{c.time}</Typography>
                                </Stack>

                                <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
                                    <Typography sx={{ fontSize: 13, color: "var(--cise-text-muted)" }} noWrap>
                                        {c.last}
                                    </Typography>
                                    {c.unread > 0 && (
                                        <Badge color="error" badgeContent={c.unread} sx={{ "& .MuiBadge-badge": { fontWeight: 800 } }} />
                                    )}
                                </Stack>
                            </Box>
                        </ListItemButton>
                    );
                })}
            </List>
        </Paper>
    );

    const ChatPanel = (
        <Paper
            elevation={0}
            sx={{
                borderRadius: 1,
                border: "1px solid var(--border)",
                background: "var(--branco)",
                overflow: "hidden",
                height: "100%",
                minHeight: 0,
                display: "flex",
                flexDirection: "column",
            }}
        >
            {/* Header do chat */}
            <Box
                sx={{
                    px: 2,
                    py: 1.3,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    borderBottom: "1px solid var(--border)",
                    background: "var(--branco)",
                }}
            >
                <Stack direction="row" alignItems="center" spacing={1.2}>
                    {isMobile && (
                        <IconButton size="small" onClick={closeChat}>
                            <ArrowBackIcon fontSize="small" />
                        </IconButton>
                    )}
                    <Avatar sx={{ width: 38, height: 38, bgcolor: "rgba(33,49,89,0.16)", color: "var(--cise-blue)" }}>
                        {initials(selected?.name)}
                    </Avatar>
                    <Box>
                        <Typography sx={{ fontWeight: 800, lineHeight: 1.1 }}>{selected?.name || "—"}</Typography>
                        {selected?.stage && (
                            <Typography sx={{ fontSize: 12, color: "var(--cise-text-muted)" }}>{selected.stage}</Typography>
                        )}
                    </Box>
                </Stack>

                <Stack direction="row" spacing={0.5}>
                    <IconButton size="small" title="Detalhes do lead" onClick={() => setDetailsOpen(true)}>
                        <InfoOutlinedIcon fontSize="small" />
                    </IconButton>
                    <IconButton size="small" title="Fechar conversa" onClick={closeChat}>
                        <CloseIcon fontSize="small" />
                    </IconButton>
                </Stack>
            </Box>

            {/* Mensagens — somente leitura */}
            <Box
                sx={{
                    flex: 1,
                    p: 2,
                    overflowY: "auto",
                    background:
                        "radial-gradient(circle at top, rgba(33,49,89,0.06), transparent 55%), var(--bg)",
                }}
            >
                {loadingMessages ? (
                    Array.from({ length: 6 }).map((_, i) => (
                        <Box key={i} sx={{ display: "flex", justifyContent: i % 2 ? "flex-end" : "flex-start", mb: 1.2 }}>
                            <Skeleton variant="rounded" height={44} width={160 + i * 15} sx={{ borderRadius: 2 }} />
                        </Box>
                    ))
                ) : messages.length === 0 ? (
                    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100%" }}>
                        <Typography sx={{ color: "var(--cise-text-muted)" }}>Nenhuma mensagem registrada.</Typography>
                    </Box>
                ) : (
                    buildMessagesWithSeparators(messages).map((item) =>
                        item.type === "separator" ? (
                            <DateSeparator key={item.key} label={item.label} />
                        ) : (
                            <MessageBubble key={`${item.timestamp}-${item.from}`} from={item.from} text={item.text} time={item.time} />
                        )
                    )
                )}
                <div ref={messagesEndRef} />
            </Box>

            {/* Barra de status — somente leitura */}
            <Box
                sx={{
                    px: 2,
                    py: 1,
                    borderTop: "1px solid var(--border)",
                    background: "var(--branco)",
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                }}
            >
                <Typography sx={{ fontSize: 12, color: "var(--cise-text-muted)", fontStyle: "italic" }}>
                    Visualização somente leitura · As respostas são enviadas pelo agente automatizado.
                </Typography>
            </Box>
        </Paper>
    );

    function LeftListSkeleton() {
        return (
            <Paper
                elevation={0}
                sx={{
                    borderRadius: 3,
                    border: "1px solid var(--border)",
                    background: "var(--branco)",
                    overflow: "hidden",
                    height: "100%",
                    display: "flex",
                    flexDirection: "column",
                }}
            >
                <Box sx={{ px: 2, py: 1.5, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                    <Skeleton variant="text" width={120} height={24} />
                    <Skeleton variant="circular" width={28} height={28} />
                </Box>

                <Box sx={{ px: 2, pb: 1.5 }}>
                    <Skeleton variant="rounded" height={36} sx={{ borderRadius: 999 }} />
                </Box>

                <Divider />

                <Box sx={{ p: 1.5, overflowY: "auto" }}>
                    {Array.from({ length: 8 }).map((_, i) => (
                        <Box key={i} sx={{ display: "flex", gap: 1.5, py: 1 }}>
                            <Skeleton variant="circular" width={44} height={44} />
                            <Box sx={{ flex: 1 }}>
                                <Skeleton variant="text" width="50%" height={20} />
                                <Skeleton variant="text" width="80%" height={18} />
                            </Box>
                        </Box>
                    ))}
                </Box>
            </Paper>
        );
    }

    function EmptyChat() {
        return (
            <Paper
                elevation={0}
                sx={{
                    borderRadius: 1,
                    border: "1px solid var(--border)",
                    background: "var(--branco)",
                    overflow: "hidden",
                    height: "100%",
                    display: "flex",
                    flexDirection: "column",
                }}
            >
                <Box
                    sx={{
                        flex: 1,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        p: 3,
                        textAlign: "center",
                        background:
                            "radial-gradient(circle at top, rgba(33,49,89,0.06), transparent 55%), var(--bg)",
                    }}
                >
                    <Box>
                        <Typography sx={{ fontWeight: 900, color: "var(--cise-blue)", fontSize: 20 }}>
                            Nenhuma conversa selecionada
                        </Typography>
                        <Typography sx={{ color: "var(--cise-text-muted)", mt: 0.5 }}>
                            Escolha uma conversa na lista à esquerda para visualizar o histórico.
                        </Typography>
                    </Box>
                </Box>
            </Paper>
        );
    }

    return (
        <Box
            sx={{
                height: "100%",
                display: "flex",
                flexDirection: "column",
                p: 1,
                overflow: "hidden",
            }}
        >
            <LeadDetailsDrawer
                open={detailsOpen}
                onClose={() => setDetailsOpen(false)}
                lead={selectedLead}
            />
            {isMobile ? (
                <Box sx={{ flex: 1, minHeight: 0, overflow: "hidden" }}>
                    {mobileView === "list" ? (
                        loading ? <LeftListSkeleton /> : LeftList
                    ) : selected ? (
                        ChatPanel
                    ) : (
                        <EmptyChat />
                    )}
                </Box>
            ) : (
                <Box
                    sx={{
                        flex: 1,
                        minHeight: 0,
                        display: "grid",
                        gridTemplateColumns: "360px 1fr",
                        gap: 1,
                        overflow: "hidden",
                    }}
                >
                    <Box sx={{ borderRight: "1px solid var(--border)", paddingRight: 1, minHeight: 0, overflow: "hidden" }}>
                        {loading ? <LeftListSkeleton /> : LeftList}
                    </Box>

                    {selected ? ChatPanel : <EmptyChat />}
                </Box>
            )}
        </Box>
    );
}
